import { CommonModule } from '@angular/common';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';
import {
  Component,
  computed,
  ContentChild,
  HostBinding,
  input,
  output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { TPipe, TRANSLATION_PREFIX } from '@pipes/t.pipe';
import { AbbButtonComponent } from '@components/abb-button/abb-button.component';
import { AbbIconComponent } from '@components/abb-icon/abb-icon.component';
import { validateForm } from '@utils/validation';

@Component({
  selector: 'abb-stepper',
  standalone: true,
  imports: [
    CommonModule,
    MatStepperModule,
    TPipe,
    AbbButtonComponent,
    AbbIconComponent,
  ],
  template: `
    <mat-stepper
      [orientation]="isVertical() ? 'vertical' : 'horizontal'"
      [linear]="isLinear()"
      #stepper
      color="primary"
      class="non-clickable-step"
      [ngClass]="{ 'vertical-layout': isVertical() }"
    >
      @if(showStepLabelsInDots()) {
      <ng-template matStepperIcon="number" let-index="index">
        <span [ngStyle]="{ fontSize: sizeCls()[index] }">{{
          stepsLabels()[index]
        }}</span>
      </ng-template>
      } @for(ctrl of stepsControls(); track ctrl; let idx = $index; let first =
      $first; let last = $last) {
      <mat-step [stepControl]="ctrl"></mat-step>
      }

      <ng-template matStepperIcon="edit">
        <abb-icon [name]="'mat-outlined/check_circle'"></abb-icon>
      </ng-template>
    </mat-stepper>
    <div
     class="pr-8 max-w-full flex flex-col flex-1 min-w-96 h-full"
      [ngClass]="{ 'pl-6': !isVertical() }"
    >
      <!-- Contenuto dello step -->
      <div class="flex-1 flex flex-col">
      @for(ctrl of stepsControls(); track ctrl; let idx = $index; let first =
      $first; let last = $last) { @if(stepper.selectedIndex === idx) {
      @if(stepContent) { @if(stepperTitle()) {
      <h2 class="text-abb-font font-semibold text-3xl py-3">
        {{
          stepperTitle() +
            (stepsLabels()[stepper.selectedIndex] != ''
              ? ' - ' + stepsLabels()[stepper.selectedIndex]
              : '')
        }}
      </h2>
      }
      <div class="flex-1">
        <ng-container
          *ngTemplateOutlet="
            stepContent;
            context: { index: stepper.selectedIndex }
          "
        ></ng-container>
      </div>
      }
        }}
      </div>
      
      <!-- Bottoni sempre visibili in fondo -->
      <div class="w-full flex flex-row gap-8 justify-end py-8 mt-auto">
        @if (stepper.selectedIndex > 0) {
        <abb-button
          [disabled]="isLoading()"
          (click)="goBack()"
          type="button"
          variant="clean"
          >{{ 'Back' | t }}</abb-button
        >
        } @else {
        <abb-button
          [disabled]="isLoading()"
          type="button"
          variant="clean"
          (click)="cancel()"
          >{{ 'cancel' | t }}</abb-button
        >
         } @if(stepper.selectedIndex === stepsControls().length - 1 && lastStepBtn) {
        <ng-container *ngTemplateOutlet="lastStepBtn"></ng-container>
        } @else {
        <abb-button class="w-32" (click)="goNext()">{{
          'Next' | t
        }}</abb-button>
        }
      </div>
      
    </div>
  `,
  styles: `
    ::ng-deep .non-clickable-step .mat-step-header {
      pointer-events: none;
      cursor: default;
    }
    ::ng-deep .mat-stepper-vertical {
      display: flex !important;
      flex-direction: column !important;
      row-gap: 9rem !important;
    }
    ::ng-deep .vertical-layout .mat-step {
      width: 72px;
    }
    ::ng-deep .mat-stepper-vertical-line::before {
      border-left-width: 3px !important;
      transform: translateX(-50%);
      top: -24px !important;
      bottom: unset !important;
      height: calc(var(--mat-stepper-header-height) + 9rem - 24px);
    }
    ::ng-deep .mat-vertical-content {
      padding: 0 !important;
    }
    ::ng-deep .mat-vertical-stepper-header .mat-step-icon {
      --mat-stepper-header-icon-background-color: var(--mat-stepper-line-color);
    }
    ::ng-deep .mat-horizontal-content-container {
      display: none
    }
    ::ng-deep .mat-horizontal-stepper-header-container .mat-step-header:last-child {
      flex-direction: row-reverse;
    }
  `,
})
export class AbbStepperComponent {
  @ViewChild('stepper') stepper!: MatStepper;
  @ContentChild('stepContent') stepContent!: TemplateRef<any>;
  @ContentChild('lastStepBtn') lastStepBtn!: TemplateRef<any>;
  @HostBinding('class')
  get hostClasses(): string {
    return `flex flex-1 ${this.isVertical() ? 'flex-row' : 'flex-col'}`;
  }
  isLoading = input<boolean>(false);
  isLinear = input<boolean>(false);
  isVertical = input<boolean>(false);
  stepperTitle = input<string | null>(null);
  stepsControls = input.required<FormGroup[]>();
  stepsLabels = input.required<string[]>();
  showStepLabelsInDots = input<boolean>(true);
  sizeCls = computed(() =>
    this.stepsLabels().map((s) => `${(16 * 2) / s.length}px`)
  );
  onValidationError = output<{
    containsRequired: boolean;
    containsInvalid: boolean;
  }>();
  onBeforeGoNextStep = input<(() => Promise<boolean>) | undefined>();
  onBeforeGoBack = input<(() => Promise<boolean>) | undefined>();
  onCancel = output<void>();

  constructor() {}

  cancel() {
    this.onCancel.emit();
  }

  changeStep(index: number) {
    this.stepper.selectedIndex = index;
  }

  async goBack() {
    const idx = this.stepper.selectedIndex - 1;
    if (idx >= 0) {
      const onBeforeGoBack = this.onBeforeGoBack();
      if (onBeforeGoBack) {
        const canContinue = await onBeforeGoBack();
        if (!canContinue) {
          return;
        }
      }
      this.changeStep(idx);
    }
  }

  stepValidated(): boolean {
    const formGroup = this.stepsControls()[this.stepper.selectedIndex];
    const error = validateForm(formGroup, (error) => {
      this.onValidationError.emit({
        containsRequired: error.requiredError,
        containsInvalid: error.invalidError,
      });
    });
    return error;
  }

  async goNext() {
    if (this.stepValidated()) {
      const idx = this.stepper.selectedIndex + 1;
      if (idx < this.stepsControls()?.length) {
        const onBeforeGoNextStep = this.onBeforeGoNextStep();
        if (onBeforeGoNextStep) {
          const canContinue = await onBeforeGoNextStep();
          if (!canContinue) {
            return;
          }
        }
        this.changeStep(idx);
      }
    }
  }
}
